import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { AuthMeDto, LoginData, LoginModel } from './models';
import { WrapperHttpService } from '../../services/wrapper-http-service';
import { AuthEndpoint } from './constants/auth-endpoint';
import { HttpHeaders } from '@angular/common/http';
import { RequestHeader } from '../../constants/headers';
import { API_URL } from '../../../services/config';
import { finalize, map, switchMap } from 'rxjs/operators';
import { mapMeDtoToAuthMeResponse } from './models/auth.model.adapter';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: WrapperHttpService) {}

  /**
   * Performs login with email and password.
   * On success, the backend sets an HttpOnly session cookie, and this method returns the user's profile data.
   */
  login({ email, password }: LoginModel): Observable<LoginData> {
    const pwdLocal = password.replace(/\s/g, '');
    return this.http
      .withLoading()
      .post<AuthMeDto>(
        `${API_URL}${AuthEndpoint.Login}`,
        {},
        {
          headers: new HttpHeaders({
            [RequestHeader.SkipInterceptor]: '',
            [RequestHeader.Authorization]: `Basic ${btoa(unescape(encodeURIComponent(`${email}:${pwdLocal}`)))}`,
          }),
        },
      )
      .pipe(
        map((dto: AuthMeDto) => ({
          dto,
          response: mapMeDtoToAuthMeResponse(dto),
        })),
      );
  }

  /**
   * Performs login using a one-time hash.
   * On success, the backend sets an HttpOnly session cookie, and this method returns the user's profile data.
   */
  loginByHash(ssoJwt: string): Observable<LoginData> {
    return this.http
      .withLoading()
      .post<{ sessionToken: string }>(
        `${API_URL}${AuthEndpoint.Session}`,
        { token10: null, token20: ssoJwt },
        {
          headers: new HttpHeaders({
            [RequestHeader.SkipInterceptor]: '',
            Authorization: `Bearer ${ssoJwt}`,
          }),
        },
      )
      .pipe(
        switchMap((session: { sessionToken: string }) => {
          return this.http.withLoading().get<AuthMeDto>(`${API_URL}${AuthEndpoint.Session}/${session.sessionToken}`, {
            headers: new HttpHeaders({
              [RequestHeader.SkipInterceptor]: '',
            }),
          });
        }),
        map((dto: AuthMeDto) => ({
          dto,
          response: mapMeDtoToAuthMeResponse(dto),
        })),
      );
  }

  /**
   * Handles user password reset requests.
   */
  resetPassword(email: string): Observable<void> {
    return this.http.withLoading().post(
      `${API_URL}${AuthEndpoint.PasswordReset}`,
      { email },
      {
        headers: new HttpHeaders({
          [RequestHeader.SkipInterceptor]: '',
        }),
      },
    );
  }

  /**
   * Fetches the current user's data. This relies on the HttpOnly session cookie
   * being automatically sent by the browser. It's the primary method for
   * verifying an active session on application startup.
   */
  private meInFlight$: Observable<LoginData> | null = null;

  me(): Observable<LoginData> {
    if (this.meInFlight$) {
      return this.meInFlight$;
    }

    this.meInFlight$ = this.http
      .withLoading()
      .get<AuthMeDto>(`${API_URL}${AuthEndpoint.AuthMe}`)
      .pipe(
        map((dto: AuthMeDto) => ({
          dto,
          response: mapMeDtoToAuthMeResponse(dto),
        })),
        finalize(() => (this.meInFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.meInFlight$;
  }

  /**
   * Calls the logout endpoint. The backend is responsible for clearing the HttpOnly session cookie.
   */
  private logoutInFlight$: Observable<void> | null = null;

  logout(): Observable<void> {
    if (this.logoutInFlight$) {
      return this.logoutInFlight$;
    }

    this.logoutInFlight$ = this.http
      .withLoading()
      .post<void>(`${API_URL}${AuthEndpoint.Logout}`, {})
      .pipe(
        finalize(() => (this.logoutInFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.logoutInFlight$;
  }

  /**
   * Calls the refresh endpoint to extend the session. The backend should update the HttpOnly session cookie's expiration.
   */
  private refreshInFlight$: Observable<void> | null = null;

  refresh(): Observable<void> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.http
      .withLoading()
      .post<void>(`${API_URL}${AuthEndpoint.Refresh}`, '')
      .pipe(
        finalize(() => (this.refreshInFlight$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshInFlight$;
  }
}
